/**
 * One named tax line of a pricing breakdown (e.g. GST + PST), as returned by
 * `bookerCartPrice`, `orderPricesById`, and payment request queries. `name`
 * may be empty (unnamed single-rate tax) and `ratePercent` is null for
 * legacy lumped amounts where the configured rate is unknown.
 */
export type TaxLineDisplay = {
  name: string;
  ratePercent?: number | null;
  amount: number;
};

/**
 * Label for a tax line row, e.g. "GST (5%)", "HST (13%)", or "Tax (13%)"
 * when the tax is unnamed. `genericTaxLabel` should be the localized "Tax"
 * string from the calling surface.
 */
export function getTaxLineLabel(
  line: Pick<TaxLineDisplay, "name" | "ratePercent">,
  genericTaxLabel: string,
): string {
  const name = line.name?.trim() || genericTaxLabel;
  const rate =
    line.ratePercent != null && line.ratePercent > 0
      ? ` (${parseFloat(line.ratePercent.toFixed(2))}%)`
      : "";
  return `${name}${rate}`;
}

/**
 * Normalizes optional/legacy tax data into displayable lines: prefers the
 * server-provided lines, falls back to a single unnamed line built from the
 * lumped tax amount (rate unknown), or none when tax is 0.
 */
export function resolveTaxLinesForDisplay(
  taxLines: TaxLineDisplay[] | null | undefined,
  taxTotal: number,
): TaxLineDisplay[] {
  if (taxLines && taxLines.length > 0) return taxLines;
  if (taxTotal > 0) return [{ name: "", ratePercent: null, amount: taxTotal }];
  return [];
}
